"use client"

import styled from "@emotion/styled"
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material"
import {
  Int32StringValueTuple,
  LostFoundColorPublicResponse,
  LostFoundContentPublicResponse,
} from "api"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { usePathname, useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import Loading from "../Common/Loading"
import { FormError } from "./ContactContent"
import Complaint from "./Forms/Complaint"
import LostFound from "./Forms/Lost&Found"
import OptimizationProposal from "./Forms/OptimizationProposal"
import Ravkav from "./Forms/Ravkav"
import ReportRecover from "./Forms/ReportRecover"
import RequestForCredit from "./Forms/RequestForCredit"
import RequestForTaxInvoice from "./Forms/RequestFotTaxInvoice"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48pxpx;
  }
`

const StyledFormControl = styled(FormControl)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 352px;
    margin-top: -8px;
  }
`

const StyledInputLabel = styled(InputLabel)`
  &[data-shrink="true"] {
    transform: translate(14px, 0px) scale(0.75);
  }
`

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
  }
`

const StyledFormControlLabel = styled(FormControlLabel)`
  font-size: 18px;
  line-height: 26px;
`

const englishLabels = [
  "Complaint",
  "Suggestion, Comment, Inquiry",
  "Report a Lost Item",
  "Rav-Kav refund request",
  "Report Retrieval (Penalty Fare)",
  "Credit card refund request",
  "Request for a tax invoice",
]

export type FormId = 1 | 2 | 3 | 4 | 5 | 6 | 7

export default function Form({
  contactTypesData,
  handleResult,
  locale,
  initialFormType,
  lostFoundContentData,
  lostFoundColorsData,
}: {
  contactTypesData: Int32StringValueTuple[]
  handleResult: (type: FormError, id?: string) => void
  locale: "he" | "en" | "ar" | "ru"
  initialFormType?: number | null
  lostFoundContentData: LostFoundContentPublicResponse[]
  lostFoundColorsData: LostFoundColorPublicResponse[]
}) {
  const hebrew = locale === "he"
  const [isLoading, setIsLoading] = useState(false)
  const tInner = useTranslations("InnerPages")
  const [subject, setSubject] = useState<FormId | "">(
    initialFormType ? (initialFormType as FormId) : "",
  )
  const router = useRouter()
  const pathname = usePathname()

  const isEmbedded = pathname?.startsWith("/embed") || false

  useEffect(() => {
    if (initialFormType) {
      setSubject(initialFormType as FormId)
    }
  }, [initialFormType])

  // Focus management when subject changes
  useEffect(() => {
    if (subject && !isLoading) {
      // Small delay to ensure the form is rendered
      setTimeout(() => {
        // Find the first input/textarea/select element in the form
        const firstInput = document.querySelector(
          'form input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([disabled]), form textarea:not([disabled]), form select:not([disabled])',
        ) as HTMLElement

        if (firstInput) {
          firstInput.focus()
        }
      }, 100)
    }
  }, [subject, isLoading])

  const handleSubjectChange = (e: any) => {
    const value = e.target.value
    setSubject(value)
    const url = new URL(window.location.href)
    if (value) {
      url.searchParams.set("form", value.toString())
    } else {
      url.searchParams.delete("form")
    }
    router.replace(url.pathname + url.search, { scroll: false })
  }

  return (
    <StyledLayout>
      {!isEmbedded && (
        <>
          <Typography variant="h4">{tInner("inquirySubject")}</Typography>

          <StyledRadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue={subject}
            value={subject}
            onChange={handleSubjectChange}
            name="radio-buttons-group"
          >
            {[
              ...contactTypesData,
              // { item1: 7, item2: tInner("lostItem") }
            ].map((el, i) => (
              <StyledFormControlLabel
                key={i}
                value={el.item1}
                control={
                  <Radio
                    icon={
                      <Image
                        width={24}
                        height={24}
                        src="/icons/radioEmpty.svg"
                        alt=""
                      />
                    }
                    checkedIcon={
                      <Image
                        width={24}
                        height={24}
                        src="/icons/radioFull.svg"
                        alt=""
                      />
                    }
                  />
                }
                label={hebrew ? el.item2 : englishLabels[i]}
              />
            ))}
          </StyledRadioGroup>
        </>
      )}
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {subject === 1 && (
            <Complaint
              handleResult={handleResult}
              locale={locale}
              contactType={1}
              setIsLoading={setIsLoading}
            />
          )}
          {subject === 2 && (
            <OptimizationProposal
              handleResult={handleResult}
              locale={locale}
              contactType={2}
              setIsLoading={setIsLoading}
            />
          )}
          {subject === 3 && (
            <LostFound
              handleResult={handleResult}
              locale={locale}
              lostFoundContentData={lostFoundContentData}
              lostFoundColorsData={lostFoundColorsData}
              setIsLoading={setIsLoading}
            />
          )}
          {subject === 4 && (
            <Ravkav
              handleResult={handleResult}
              locale={locale}
              contactType={4}
              setIsLoading={setIsLoading}
            />
          )}
          {subject === 5 && (
            <ReportRecover
              handleResult={handleResult}
              locale={locale}
              contactType={5}
              setIsLoading={setIsLoading}
            />
          )}
          {subject === 6 && (
            <RequestForCredit
              handleResult={handleResult}
              locale={locale}
              contactType={6}
              setIsLoading={setIsLoading}
            />
          )}
          {subject === 7 && (
            <RequestForTaxInvoice
              handleResult={handleResult}
              locale={locale}
              contactType={7}
              setIsLoading={setIsLoading}
            />
          )}
        </>
      )}
    </StyledLayout>
  )
}
